/**
 * Draft-first suggestions — pure composers that turn a nudge/finding into
 * the FINISHED artifact, so suggests/do-now cards stop nagging ("Queue
 * reminder task", "Check in") and instead carry the actual email with
 * [Approve & send] [Edit] [Skip].
 *
 * Honesty contract (same discipline as src/lib/cs/weeklyUpdate.ts):
 *   - every fact in a draft comes from a REAL record field — amount, due
 *     date, invoice label, follow-up date, next step. Nothing invented.
 *   - optional fields that are absent are OMITTED, never padded.
 *   - a record missing the essentials → the composer returns null and the
 *     card falls back to the old queue-task button (an honest fallback,
 *     not a fake draft).
 *   - recipient resolution reuses `resolveCustomerUpdateRecipient`; no
 *     email on file → the card shows the honest "needs email" state
 *     instead of a draft. Never a guessed address.
 *
 * Sending stays approval-gated: the drafts here become `send_drafted_email`
 * proposals in the EXISTING approvals inbox (queueApproval → resolveApproval
 * → outbox), mirroring `send_customer_update` precisely. Nothing in this
 * module writes anything — PURE by contract: no store, no network, no clock
 * reads unless `now` is omitted, inputs untouched.
 */

import type { BillingDate, Person } from '../db/types';
import type { Nudge } from './proactiveNudges';
import type { WeeklyOpsItem } from '../crm/weeklyOps';
import type { SendDraftedEmailAction } from './types';
import { escapeHtml } from '../crm/weeklyUpdate';
import {
  resolveCustomerUpdateRecipient,
  type CustomerUpdateRecipient,
} from '../cs/weeklyUpdate';

const DAY_MS = 86_400_000;

/** What a drafted email is — drives the confirm-card verb + activity log. */
export type DraftIntent = 'payment_reminder' | 're_engagement' | 'check_in';

/** Human label per intent, shared by cards, confirm cards, and the log. */
export const DRAFT_INTENT_LABEL: Record<DraftIntent, string> = {
  payment_reminder: 'payment reminder',
  re_engagement: 'follow-up email',
  check_in: 'check-in email',
};

/** A finished, ready-to-approve email artifact. */
export interface ComposedDraft {
  intent: DraftIntent;
  subject: string;
  /** Plain-text body — the editable source of truth. */
  text: string;
  /** Inline-styled HTML rendering of `text` (Gmail-readable). */
  html: string;
}

// --- helpers -----------------------------------------------------------------

function nonBlank(s: string | undefined): string | undefined {
  const t = s?.trim();
  return t ? t : undefined;
}

/** Whole days a date (YYYY-MM-DD or ISO) is past `now`; ≤ 0 = not yet. */
function daysPast(iso: string, now: number): number {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.floor((now - t) / DAY_MS);
}

function usd(n: number): string {
  return `$${n.toLocaleString('en-US')}`;
}

/** First name to greet — the resolved recipient first, then the record. */
function greetingName(person: Person): string {
  const who = resolveCustomerUpdateRecipient(person)?.name ?? person.name;
  return who.trim().split(/\s+/)[0];
}

/**
 * Render a plain-text body as minimal, inline-styled HTML (Gmail-safe).
 * Paragraphs split on blank lines; single newlines become <br/>. Exported
 * so an operator-edited body re-renders through the SAME escaping path.
 */
export function draftTextToHtml(text: string): string {
  const paras = text
    .split(/\n{2,}/)
    .map(
      (p) =>
        `<p style="margin:0 0 12px;">${escapeHtml(p).replace(/\n/g, '<br/>')}</p>`,
    );
  return (
    `<div style="font-family:-apple-system,'Segoe UI',Helvetica,Arial,sans-serif;` +
    `max-width:600px;margin:0 auto;padding:24px;color:#1a1a1a;background:#ffffff;` +
    `line-height:1.5;">${paras.join('')}</div>`
  );
}

function finishDraft(intent: DraftIntent, subject: string, lines: string[]): ComposedDraft {
  const text = lines.join('\n');
  return { intent, subject, text, html: draftTextToHtml(text) };
}

// --- composers ---------------------------------------------------------------

/**
 * Warm, specific past-due invoice reminder. Essentials: a named person, a
 * labelled billing record, and a parseable due date that is genuinely past
 * (≥ 1 whole day). Amount is included only when the record carries one.
 * Returns null when any essential is missing — no fake drafts.
 */
export function composePastDueReminder(
  person: Person,
  invoice: BillingDate,
  now: number = Date.now(),
): ComposedDraft | null {
  const label = nonBlank(invoice.label);
  if (!nonBlank(person.name) || !label) return null;
  if ((invoice.status ?? 'scheduled') === 'paid') return null;
  const days = daysPast(invoice.date, now);
  if (days < 1) return null;

  const amount = invoice.amount != null ? usd(invoice.amount) : undefined;
  const dayWord = `${days} day${days === 1 ? '' : 's'}`;
  const subject = `Quick reminder: ${label} is ${dayWord} past due`;
  const lines = [
    `Hi ${greetingName(person)},`,
    '',
    `Just a friendly nudge — ${label}${amount ? ` for ${amount}` : ''} was due ${invoice.date}, which puts it ${dayWord} past due.`,
    '',
    `If payment is already on its way, thank you — please ignore this. Otherwise, is there anything you need from us to get it over the line?`,
    '',
    'Thanks!',
  ];
  return finishDraft('payment_reminder', subject, lines);
}

/** The last-touch context a re-engagement draft is grounded in. */
export interface LastTouchInfo {
  /** The follow-up date that slipped (YYYY-MM-DD or ISO). Essential. */
  followUpDate: string;
  /** The agreed next step, when the record carries one. */
  nextStep?: string;
}

/**
 * Follow-up email for a relationship whose follow-up date fully slipped.
 * Essentials: a named person and a parseable follow-up date at least one
 * whole day past. The next-step line appears only when the record has one.
 */
export function composeReengagement(
  person: Person,
  lastTouch: LastTouchInfo,
  now: number = Date.now(),
): ComposedDraft | null {
  if (!nonBlank(person.name)) return null;
  const days = daysPast(lastTouch.followUpDate, now);
  if (days < 1) return null;

  const nextStep = nonBlank(lastTouch.nextStep);
  const subject = `Following up — picking things back up`;
  const lines = [
    `Hi ${greetingName(person)},`,
    '',
    `We had a follow-up on the books for ${lastTouch.followUpDate.slice(0, 10)} and I didn't want it to slip past us.`,
  ];
  if (nextStep) {
    lines.push('', `The next step on our side was: ${nextStep}.`);
  }
  lines.push(
    '',
    'Do you have 15 minutes this week to pick things back up?',
    '',
    'Thanks!',
  );
  return finishDraft('re_engagement', subject, lines);
}

/** The health context an at-risk check-in is grounded in. The band comes
 *  from the EXISTING health scoring (crm/health.ts) — never invented here. */
export interface CheckInContext {
  band: 'at-risk' | 'watch';
}

/**
 * At-risk customer check-in. Deliberately does NOT quote the internal
 * health score at the customer — the health signal justifies the send;
 * the body is a plain, warm offer to talk. Essentials: a named, live
 * customer (not churned, not a prospect).
 */
export function composeCheckIn(
  person: Person,
  _health: CheckInContext,
  _now: number = Date.now(),
): ComposedDraft | null {
  if (!nonBlank(person.name)) return null;
  if (person.category !== 'customer' || person.status === 'churned') return null;

  const company = nonBlank(person.company);
  const subject = `Checking in${company ? ` on ${company}` : ''} — how are things going?`;
  const lines = [
    `Hi ${greetingName(person)},`,
    '',
    `It's been a little while since we last connected, and I wanted to check in personally — how are things going on your side${company ? ` at ${company}` : ''}?`,
    '',
    `If anything feels off, or you'd like to walk through anything together, I'd love to grab 20 minutes this week.`,
    '',
    'Thanks!',
  ];
  return finishDraft('check_in', subject, lines);
}

/** Which stage-grounded nudge a kanban attention flag calls for. */
export type StageNudgeKind =
  | 're_engagement'
  | 'proposal_follow_up'
  | 'kickoff_check_in'
  | 'cs_check_in';

/** The account-side contact a stage nudge addresses — the board's POC
 *  fields, NOT a Person record (accounts carry the POC inline). */
export interface StageNudgeContact {
  name: string;
  email?: string;
}

/** The real record facts a stage nudge is grounded in. */
export interface StageNudgeContext {
  kind: StageNudgeKind;
  accountName: string;
  workflowName: string;
  /** The workflow's current pipeline stage (provenance, not quoted verbatim). */
  stage: string;
  /** Whole days since the account record last moved — computed by the
   *  caller from the REAL `updatedAt` (see crm/boardAttention.ts). */
  daysIdle: number;
}

/**
 * Generic stage nudge for a stalled kanban card — the composer behind the
 * board's attention chips, used because the three record-shaped composers
 * above need Person fields an Account card doesn't carry. Essentials: a
 * named contact, a named account + workflow, and at least one genuine
 * whole day of idleness. Returns null when any essential is missing.
 */
export function composeStageNudge(
  contact: StageNudgeContact,
  ctx: StageNudgeContext,
): ComposedDraft | null {
  const who = nonBlank(contact.name);
  const accountName = nonBlank(ctx.accountName);
  const workflowName = nonBlank(ctx.workflowName);
  if (!who || !accountName || !workflowName) return null;
  if (!Number.isFinite(ctx.daysIdle) || ctx.daysIdle < 1) return null;

  const first = who.split(/\s+/)[0];
  const dayWord = `${ctx.daysIdle} day${ctx.daysIdle === 1 ? '' : 's'}`;

  switch (ctx.kind) {
    case 're_engagement':
      return finishDraft('re_engagement', `Picking ${workflowName} back up`, [
        `Hi ${first},`,
        '',
        `It's been ${dayWord} since we last moved ${workflowName} forward together, and I didn't want it to slip past us.`,
        '',
        'Do you have 15 minutes this week to pick things back up?',
        '',
        'Thanks!',
      ]);
    case 'proposal_follow_up':
      return finishDraft('re_engagement', `Following up on the ${workflowName} proposal`, [
        `Hi ${first},`,
        '',
        `I wanted to follow up on where we landed with ${workflowName} — it's been ${dayWord} since our last movement and I'd love to keep things on track.`,
        '',
        `Happy to answer any questions or walk through the proposal again — do you have 15 minutes this week?`,
        '',
        'Thanks!',
      ]);
    case 'kickoff_check_in':
      return finishDraft('check_in', `Kickoff check-in — ${workflowName}`, [
        `Hi ${first},`,
        '',
        `Quick check-in on ${workflowName} — it's been ${dayWord} since our last update on the ${accountName} side, and I want to make sure kickoff keeps moving.`,
        '',
        'Is there anything you need from us to keep things on track?',
        '',
        'Thanks!',
      ]);
    case 'cs_check_in':
      return finishDraft('check_in', `Checking in on ${workflowName}`, [
        `Hi ${first},`,
        '',
        `It's been ${dayWord} since we last connected on ${workflowName}, and I wanted to check in personally — how are things going on your side?`,
        '',
        `If anything feels off, or you'd like to walk through anything together, I'd love to grab 20 minutes this week.`,
        '',
        'Thanks!',
      ]);
  }
}

// --- card wiring: nudge/ops-item → draft state ---------------------------------

/** What a suggests/do-now card renders for one suggestion. */
export type NudgeDraftState =
  /** A finished draft + resolved recipient, ready to queue for approval. */
  | {
      state: 'draft';
      recipient: CustomerUpdateRecipient;
      action: SendDraftedEmailAction;
    }
  /** A draft WOULD exist, but no email is on file — the honest state. */
  | { state: 'needs-email'; personName: string };

/** Worst (most days past due) unpaid billing record — same rule as the
 *  invoice_overdue nudge collector, so card and nudge agree. */
function worstOverdueInvoice(person: Person, now: number): BillingDate | undefined {
  return (person.billingDates ?? [])
    .filter((bd) => (bd.status ?? 'scheduled') !== 'paid' && daysPast(bd.date, now) > 0)
    .sort((a, b) => daysPast(b.date, now) - daysPast(a.date, now))[0];
}

function composeForKind(
  kind: string,
  person: Person,
  now: number,
): ComposedDraft | null {
  switch (kind) {
    case 'invoice_overdue': {
      const worst = worstOverdueInvoice(person, now);
      return worst ? composePastDueReminder(person, worst, now) : null;
    }
    case 'followup_overdue':
    case 'overdue_followup': {
      if (!person.followUpDate) return null;
      return composeReengagement(
        person,
        { followUpDate: person.followUpDate, nextStep: person.nextStep },
        now,
      );
    }
    case 'at_risk_customer':
    case 'at_risk':
      // The nudge/ops kind itself comes from the existing health engine —
      // that signal (not a re-score here) grounds the at-risk band.
      return composeCheckIn(person, { band: 'at-risk' }, now);
    default:
      return null;
  }
}

function draftStateFor(
  kind: string,
  personId: string | undefined,
  people: Person[],
  now: number,
): NudgeDraftState | null {
  if (!personId) return null;
  const person = people.find((p) => p.id === personId);
  if (!person) return null;
  const draft = composeForKind(kind, person, now);
  if (!draft) return null;
  const recipient = resolveCustomerUpdateRecipient(person);
  if (!recipient) return { state: 'needs-email', personName: person.name };
  return {
    state: 'draft',
    recipient,
    action: {
      kind: 'send_drafted_email',
      personId: person.id,
      personName: person.name,
      intent: draft.intent,
      to: recipient.email,
      subject: draft.subject,
      text: draft.text,
      html: draft.html,
    },
  };
}

/**
 * Draft-first state for an "Argo suggests" nudge. Returns:
 *   - `{state:'draft', …}` when a finished artifact composes from the record
 *   - `{state:'needs-email', …}` when it composes but no address resolves
 *   - null when this nudge kind has no email artifact or the record lacks
 *     essentials — the card keeps its old queue-task button.
 */
export function draftForNudge(
  nudge: Nudge,
  people: Person[],
  now: number = Date.now(),
): NudgeDraftState | null {
  return draftStateFor(nudge.kind, nudge.personId, people, now);
}

/**
 * Draft-first state for a Do-now (weekly ops) row. Person-tied kinds carry
 * the person id inside the stable item id (`weekly-ops:<kind>:<entityId>`).
 */
export function draftForOpsItem(
  item: WeeklyOpsItem,
  people: Person[],
  now: number = Date.now(),
): NudgeDraftState | null {
  const m = /^weekly-ops:(at_risk|overdue_followup):(.+)$/.exec(item.id);
  if (!m) return null;
  return draftStateFor(m[1], m[2], people, now);
}
